import { TAU, hand, pt, ring, rng, tremor, type Pt } from './geom';
import { dot, fillPoly, type Paint, type Prim } from './spec';

/**
 * **笔迹** —— 记号笔、圆珠笔、涂鸦。
 *
 * ⚠️ 手绘感的第一来源是**粗细不匀**，不是抖动。
 * 一根等宽的线，抖得再厉害也是"电脑画的抖动线"；
 * 一根粗细变化的线，不抖就已经像人画的了。
 *
 * 所以这里做的核心一件事：**把中心线加粗成一条粗细变化的实心带**。
 * 结果是一个普通多边形 —— 也就是说笔迹在 IR 里没有任何特殊地位，
 * painter 根本不知道自己在画"笔迹"。
 */

export interface InkOpts {
  /**
   * 线宽（设计单位）。可以是常数，也可以是 t∈[0,1] 的函数。
   * ⚠️ 用函数就能做"渐粗渐细" —— 蛇的身子、锄头的柄都靠它。
   */
  readonly w?: number | ((t: number) => number);
  /** 粗细变化的幅度。0 = 等宽（马克笔）；0.3 = 明显的手压变化（圆珠笔） */
  readonly varied?: number;
  /** 粗细变化的频率。高 = 一段线里粗细反复好几次 */
  readonly freq?: number;
}

/** 笔迹的几何：一条带 + 两头的圆帽 */
export interface InkShape {
  readonly body: Pt[];
  readonly caps: readonly { readonly x: number; readonly y: number; readonly r: number }[];
}

const MIN_W = 0.28;

/**
 * 中心线 → 粗细不匀的笔迹带。
 *
 * 法线用**相邻两点的差分**算，所以它和 `hand()` 抖过的路径是配套的：
 * 先抖路径再算笔迹，得到的才是"手在抖、笔压也在变"。
 */
export function inkStroke(pts: readonly Pt[], seed: number, o: InkOpts = {}): InkShape {
  const w = o.w ?? 1.7;
  const varied = o.varied ?? 0.32;
  const freq = o.freq ?? 2.6;
  const ph = rng(seed ^ 0x2f1b)() * TAU;
  const nz = tremor(seed ^ 0x5bf03635, 1, 1.4);
  const m = pts.length;
  if (m < 2) return { body: [...pts], caps: [] };

  const wid = (i: number): number => {
    const t = i / (m - 1);
    const base = typeof w === 'function' ? w(t) : w;
    return Math.max(MIN_W, base * (1 + varied * Math.sin(t * TAU * freq + ph) + 0.32 * nz(t)));
  };

  const up: Pt[] = [];
  const dn: Pt[] = [];
  for (let i = 0; i < m; i++) {
    const p = pts[Math.max(0, i - 1)]!;
    const q = pts[Math.min(m - 1, i + 1)]!;
    let dx = q.x - p.x;
    let dy = q.y - p.y;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    const ww = wid(i);
    const c = pts[i]!;
    up.push(pt(c.x - dy * ww, c.y + dx * ww));
    dn.push(pt(c.x + dy * ww, c.y - dx * ww));
  }
  const body = up.concat(dn.reverse());
  const caps = [
    { x: pts[0]!.x, y: pts[0]!.y, r: wid(0) },
    { x: pts[m - 1]!.x, y: pts[m - 1]!.y, r: wid(m - 1) },
  ];
  return { body, caps };
}

/** 笔迹 → IR 图元（一条填充带 + 两个圆帽） */
export function inkPrims(
  pts: readonly Pt[],
  seed: number,
  paint: Paint,
  o: InkOpts = {},
): Prim[] {
  const s = inkStroke(pts, seed, o);
  const out: Prim[] = [fillPoly(s.body, paint)];
  for (const c of s.caps) out.push(dot(c.x, c.y, c.r, paint));
  return out;
}

// ============================================================
// 常用构件
// ============================================================

/**
 * 阿基米德螺线：r 随转角线性内收。
 * 囚者颈环用它 —— "循环没有闭合"，中心是它从没到达的那一点。
 */
export function spiral(
  cx: number,
  cy: number,
  r0: number,
  r1: number,
  a0: number,
  a1: number,
  n: number,
): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const a = a0 + (a1 - a0) * t;
    const r = r0 + (r1 - r0) * t;
    out.push(pt(cx + r * Math.cos(a), cy + r * Math.sin(a)));
  }
  return out;
}

/**
 * 四芒星。素人画的星星就长这样 —— 不是五角星，是四道芒。
 * ⚠️ 转 45°。不转就读成"医疗十字"（踩过）。
 */
export function sparkle(cx: number, cy: number, r: number, seed: number): Pt[][] {
  const j = tremor(seed, 1.0, 1.7);
  const rays: Pt[][] = [];
  for (let k = 0; k < 4; k++) {
    const a = (k * Math.PI) / 2 + Math.PI / 4;
    const rr = r * (k % 2 === 0 ? 1 : 0.52);
    const e = j(k / 4);
    rays.push([pt(cx, cy), pt(cx + Math.cos(a) * rr + e, cy + Math.sin(a) * rr - e)]);
  }
  return rays;
}

/** 臭气线：一条往上摆的波浪。素人画"可疑的东西"的标准配件 */
export function stink(x: number, y0: number, y1: number, amp: number, waves: number, seed: number): Pt[] {
  const out: Pt[] = [];
  const nz = tremor(seed, amp * 0.25, 2.2);
  for (let i = 0; i <= 16; i++) {
    const t = i / 16;
    out.push(pt(x + Math.sin(t * TAU * waves) * amp + nz(t), y0 + (y1 - y0) * t));
  }
  return out;
}

/** 叉叉眼：素人艺术表示"死了"的标准画法 */
export function xEyes(cx: number, cy: number, r: number, seed: number): Pt[][] {
  return [
    hand([pt(cx - r, cy - r), pt(cx + r, cy + r)], seed, 0.4, 3),
    hand([pt(cx + r, cy - r), pt(cx - r, cy + r)], seed ^ 0xcc, 0.4, 3),
  ];
}

/** 一个手画的圆（不是 `ring()` 那种理想圆，是抖过的） */
export function handRing(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  seed: number,
  n = 44,
  amp = 1.2,
  freq = 1.5,
): Pt[] {
  return hand(ring(cx, cy, rx, ry, n), seed, amp, freq);
}
